import {DataTable, Table} from '@primer/react/experimental'
import {useMemo} from 'react'

import styles from './TraceTable.module.css'
import {type DecoratedNode, filterTreeByVerbosity, fixTimes} from './utilities/tree-utilities'

export function TraceTable({
  root,
  verboseMode,
}: {
  root: {node: DecoratedNode; minTime: number; maxTime: number; maxDepth: number}
  verboseMode: boolean
}) {
  const tableRows = useMemo(() => {
    const filtered = filterTreeByVerbosity(root.node, verboseMode)
    if (!filtered) return []
    const r = fixTimes(filtered, root.minTime, root.maxTime)
    const t = makeTable(r)
    return t
  }, [root, verboseMode])

  return (
    <div className={styles.traceTable}>
      {tableRows.length > 0 ? (
        <Table.Container>
          <Table.Title id="trace-table">Trace Table</Table.Title>
          <DataTable
            data={tableRows}
            columns={[
              {header: 'Name', field: 'id', sortBy: 'alphanumeric'},
              {header: 'Count', field: 'count', sortBy: 'basic'},
              {
                header: 'Average Duration',
                field: 'avgDuration',
                sortBy: 'basic',
                renderCell: row => row.avgDuration.toFixed(2),
              },
              {
                header: 'Total Duration',
                field: 'totalDuration',
                sortBy: 'basic',
                renderCell: row => row.totalDuration.toFixed(2),
              },
            ]}
            initialSortColumn={'totalDuration'}
            initialSortDirection="DESC"
          />
        </Table.Container>
      ) : (
        <div style={{padding: '16px', textAlign: 'center', color: '#666'}}>
          No spans to display. Try enabling verbose mode to see all spans.
        </div>
      )}
    </div>
  )
}

interface TableRow {
  id: string
  count: number
  avgDuration: number
  totalDuration: number
}

function makeTable(root: DecoratedNode): TableRow[] {
  const map = new Map<string, TableRow>()
  function traverse(node: DecoratedNode) {
    let value = map.get(node.name)
    if (!value) {
      value = {id: node.name, count: 0, avgDuration: 0, totalDuration: 0}
    }
    value.count += 1
    value.totalDuration += node.duration
    value.avgDuration = value.totalDuration / value.count
    map.set(node.name, value)
    // eslint-disable-next-line github/array-foreach
    node.children.forEach(traverse)
  }
  traverse(root)
  return Array.from(map.values()).sort((a, b) => b.totalDuration - a.totalDuration)
}
